import { useRef } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import type { CredentialsUpsertRequest } from '@customerio/hydra-ui-api';
import { type PushActionType, type PushApiError, type PushErrorType } from '@/features/push-credentials';
import { findPushPlatform, type PushPlatform, type PushPlatformDetails } from '@/features/push-credentials';
import {
  useDeleteActionCredential,
  useUpdateActionCredentialConfig,
  useUpsertActionCredential,
} from '@/services/db/action-credential/mutations';
import type { PushSaveContext } from '../types';
import { pushErrorsOrFallback, SAVE_FAILED_MESSAGE } from '../utils/push-errors';

export type PushCredentialBody = CredentialsUpsertRequest['credential'];

export type PushCredentialWrites = {
  upsert: (actionType: PushActionType, credential: PushCredentialBody) => Promise<void>;
  setConfig: (actionType: PushActionType, config: string) => Promise<void>;
  remove: (actionType: PushActionType) => Promise<void>;
  isWriting: boolean;
};

/**
 * Each write reloads the credential slot it touched and the environment, so the
 * platforms the page reads back are the ones the write produced.
 */
export function usePushCredentialWrites(workspaceId: number): PushCredentialWrites {
  const options = { workspaceId };
  const upsert = useUpsertActionCredential(options);
  const config = useUpdateActionCredentialConfig(options);
  const remove = useDeleteActionCredential(options);

  return {
    upsert: (actionType, credential) => upsert.mutateAsync({ actionType, credential }),
    setConfig: (actionType, value) => config.mutateAsync({ actionType, config: value }),
    remove: (actionType) => remove.mutateAsync({ actionType }),
    isWriting: upsert.isPending || config.isPending || remove.isPending,
  };
}

export function pushSaveContext(platforms: readonly PushPlatformDetails[], platform: PushPlatform): PushSaveContext {
  return {
    wasConfigured: findPushPlatform(platforms, platform) != null,
    hadFcmCredential: platforms.some((entry) => entry.provider === 'fcm'),
  };
}

type RunSaveOptions = {
  write: () => Promise<void>;
  onSaved: () => void;
};

/**
 * The guard is a ref rather than the mutation's pending flag because a second
 * click can land before React re-renders, and two clicks must not become two credentials.
 */
export function usePushSaveRunner(
  addErrors: (type: PushErrorType, errors: readonly PushApiError[]) => void,
): (options: RunSaveOptions) => Promise<void> {
  const snackbars = useSnackbars();
  const isRunningRef = useRef(false);

  return async ({ write, onSaved }: RunSaveOptions) => {
    if (isRunningRef.current) {
      return;
    }
    isRunningRef.current = true;

    try {
      await write();
    } catch (error) {
      addErrors('config', pushErrorsOrFallback(error, SAVE_FAILED_MESSAGE));
      return;
    } finally {
      isRunningRef.current = false;
    }

    snackbars.create({ variant: 'default', id: 'push.configuration.saved', message: 'Configuration saved.' });
    onSaved();
  };
}
